/**
 * Where the window keeps its user's `dvk_` inference credential.
 *
 * The server's spawned CLI calls the inference proxy, reading the credential
 * from its own environment. This cache exists so a server restart does not
 * cost a freshly minted key on every bootstrap: the server holds
 * the credential in memory only, so it forgets it while `localStorage` still has
 * it, and minting per bootstrap would leave a live key behind each time.
 *
 * Holding a `dvk_` in renderer `localStorage` is an accepted risk: a key
 * extracted from the client can be used outside the app. It is mitigated by
 * per-request digest validation, entitlement admission at the proxy, IP/device
 * attribution, and independent revocation of the key. Sign-out revokes and
 * clears it, so the window is one session rather than forever.
 *
 * The record carries two things beside the key, and both are load-bearing:
 *
 * - The credential's **id**. A plaintext key cannot be matched against
 *   `GET /api/me/keys`, which returns metadata only, so the id is what lets the
 *   window notice a revoked key and what sign-out revokes.
 * - The **user** it was minted for. Accounts share a browser's origin; a key is
 *   presented only for its own user, because the server bills every turn of a
 *   user to the key pushed for them.
 */

import { accountLocalStorage } from "../../accountStorage";

const INFERENCE_KEY_STORAGE_KEY = "dovod.inference-key";

export interface HeldKey {
  readonly id: string;
  readonly key: string;
  readonly userId: string;
}

/** Returns what is held, or `null` when nothing usable is. */
export function getStoredKey(): HeldKey | null {
  const raw = accountLocalStorage.getItem(INFERENCE_KEY_STORAGE_KEY);
  if (raw === null || raw.length === 0) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "id" in parsed &&
      "key" in parsed &&
      "userId" in parsed &&
      typeof parsed.id === "string" &&
      typeof parsed.key === "string" &&
      typeof parsed.userId === "string"
    ) {
      return { id: parsed.id, key: parsed.key, userId: parsed.userId };
    }
  } catch {
    // Falls through: an unparseable value is not a key we can present.
  }
  return null;
}

/** Persists what `POST /api/me/keys` returned once, for the user it was minted for. */
export function storeKey(held: HeldKey): void {
  accountLocalStorage.setItem(INFERENCE_KEY_STORAGE_KEY, JSON.stringify(held));
}

/** Forgets the held key (sign-out, revocation, another user, or a key that cannot be verified). */
export function clearKey(): void {
  accountLocalStorage.removeItem(INFERENCE_KEY_STORAGE_KEY);
}
